import { Box, Grid, LinearProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { DEFAULT_CORP_INNCODE } from "utils/CommonConstants";
import { getHoursAndMin } from "utils/CommonFunctions";
import { getDate, getMonth } from "utils/DateHelper";
import { routes } from "utils/routes";
import Timing from "../../common/Timing";
import Typography from "../../common/Typography";
import { TAB } from "./BookmarkPlansProperties/TAB";
import PlanBadge from "./PlanBadge";

interface Props {
  type?: string;
  tab?: string;
  planName?: string;
  showDetails?: boolean;
  dateDetails?: any;
  element: any;
  url?: string;
  sx?: object;
  shouldNavigate?: boolean;
  showInncode?: boolean;
  showPlanDate?: string;
  navigateState?: any;
  isCallFromDashboard?: boolean;
}
function ActionBar(props: Props) {
  const {
    type,
    tab,
    planName,
    showDetails,
    dateDetails,
    element,
    url = routes.MyPlans,
    sx,
    shouldNavigate = true,
    showInncode = false,
    showPlanDate = "",
    navigateState,
    isCallFromDashboard = false
  } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const [searchParams] = useSearchParams();
  const selectedInncode = searchParams.get("inncode");
  const showDate = dateDetails && (element?.cardDueDate || element?.brandDeadlines?.[0]?.deadline);
  const totalCards = element?.inProgressCards + element?.cardsCompleted + element?.cardBacklog;
  const cardsCompletedPercent = totalCards > 0 ? Math.round((element?.cardsCompleted / totalCards) * 100) : 0;

  return (
    <Box
      onClick={
        shouldNavigate
          ? () => {
              navigate(url, {
                state: {
                  data: { tab, type },
                  backURL: `${routes.Dashboard}`,
                  innCode: selectedInncode || isCorp ? DEFAULT_CORP_INNCODE : "",
                  ...navigateState
                }
              });
            }
          : () => null
      }
      sx={{
        display: "flex",
        cursor: shouldNavigate ? "pointer" : "auto",
        margin: "8px 0",
        padding: dateDetails ? "16px" : "8px 16px",
        alignItems: "center",
        height: dateDetails ? "96px" : "90px",
        border: `1px solid ${tokens.Border.General.Tertiary}`,
        borderRadius: "8px",
        ...sx
      }}
    >
      <Grid
        container
        sx={{
          alignItems: "center",
          display: "flex",
          gap: dateDetails ? "16px" : "unset",
          justifyContent: "space-between"
        }}
      >
        {showDate && (
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <Typography variant="h4" color={tokens.Text.General.Inverse}>
              {getDate(element?.brandDeadlines?.[0]?.deadline)}
            </Typography>

            <Typography
              variant="body2"
              color={tokens.Text.General.Inverse}
              sx={{
                textTransform: "uppercase"
              }}
            >
              {getMonth(element?.brandDeadlines?.[0]?.deadline)}
            </Typography>
          </Grid>
        )}
        <Grid item xs={true}>
          <Box marginBottom={"4px"} display="flex" flexDirection="row">
            <PlanBadge data={element} />
            {showPlanDate && (
              <Typography
                component={"div"}
                variant="body2"
                color={colors.Neutral.Neutral400}
                sx={showPlanDate ? { marginRight: "auto" } : { marginLeft: "auto" }}
              >
                <Timing duration={getHoursAndMin(element.timeEstimateMinutes)} />
              </Typography>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              minHeight: planName ? "20px" : "43px",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: planName ? "1" : "2",
              WebkitBoxOrient: "vertical"
            }}
          >
            {element?.name}
          </Typography>

          {planName && (
            <Typography variant="label" color={tokens.Text.General.Tertiary} className="ellipse-two">
              From Plan: {planName}
            </Typography>
          )}
        </Grid>
        {showInncode && (
          <Typography variant="body2" showBold color={tokens.Text.General.Secondary}>
            {element?.innCode}
          </Typography>
        )}
        {showDetails && (
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignSelf: "flex-end",
              height: "150%"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                gap: "4px",
                marginBottom: "24px"
              }}
            >
              {tab === TAB.plans && (
                <Typography component={"div"} variant="body2" color={colors.Neutral.Neutral400}>
                  <Timing duration={getHoursAndMin(element?.remainingPlanDuration)} />
                </Typography>
              )}
            </Box>
            {tab === TAB.plans && (
              <Box>
                <Typography variant="body2" color={colors.Neutral.Neutral400} showBold>
                  {cardsCompletedPercent}% complete
                </Typography>
                <LinearProgress variant="determinate" value={cardsCompletedPercent} />
              </Box>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default ActionBar;
